import { type ReactNode, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import {
  getGetVersionV1TaxonomyVersionGetQueryKey,
  getListNodesV1TaxonomyNodesGetQueryKey,
  useCreateNodeV1TaxonomyNodesPost,
  useDeleteNodeV1TaxonomyNodesNodeIdDelete,
  useGetVersionV1TaxonomyVersionGet,
  useImportTreeV1TaxonomyImportPost,
  useLaunchTaxonomyClassifyV1TaxonomyClassifyPost,
  useListNodesV1TaxonomyNodesGet,
  useUpdateNodeV1TaxonomyNodesNodeIdPatch,
} from "../api/generated/qcsControlPlane";
import type {
  TaxonomyClassifyLaunchResult,
  TaxonomyImportResult,
  TaxonomyNodeOut,
} from "../api/generated/model";
import { useCanWrite } from "../auth/me";
import { useBreadcrumbTail } from "../components/breadcrumbs";
import { IconChevronDown, IconPlus } from "../components/icons";
import { useToast } from "../components/toast";
import { Button, EmptyState, ErrorState, PageHeader, Panel, Row, Skeleton, SkeletonRegion, Stack } from "@akaike/ui";

function errMsg(error: unknown, fallback: string): string {
  if (error && typeof error === "object" && "message" in error) {
    const m = (error as { message?: unknown }).message;
    if (typeof m === "string" && m) return m;
  }
  return fallback;
}

type Draft = {
  id?: string;
  parentId: string | null;
  name: string;
  description: string;
  notes: string;
  sortOrder: string;
  isActive: boolean;
};

function toDraft(n: TaxonomyNodeOut): Draft {
  return {
    id: n.id,
    parentId: n.parent_id,
    name: n.name,
    description: n.description,
    notes: n.notes,
    sortOrder: String(n.sort_order),
    isActive: n.is_active,
  };
}

const newDraft = (parentId: string | null): Draft => ({
  parentId,
  name: "",
  description: "",
  notes: "",
  sortOrder: "0",
  isActive: true,
});

export function Taxonomy() {
  const canWrite = useCanWrite();
  useBreadcrumbTail([{ label: "Taxonomy" }]);
  const notify = useToast();
  const qc = useQueryClient();
  const navigate = useNavigate();

  // planning/47: ad-hoc taxonomy classify launcher. A single launched run navigates to the
  // run monitor; a fan-out (all platforms) toasts a per-platform summary.
  const classify = useLaunchTaxonomyClassifyV1TaxonomyClassifyPost({
    mutation: {
      onSuccess: (result: TaxonomyClassifyLaunchResult) => {
        const launched = (result.items ?? []).filter((i) => i.status === "launched");
        if (launched.length === 1 && launched[0].run_id) {
          notify("Classify started", "ok");
          navigate(`/runs/${launched[0].run_id}`);
        } else if (launched.length > 0) {
          notify(`Started ${launched.length} classify run(s): ${launched.map((i) => i.platform).join(", ")}`, "ok");
        } else {
          notify("already running", "info");
        }
      },
      onError: (e) => notify(e instanceof Error ? e.message : "launch failed", "crit"),
    },
  });

  const nodesQ = useListNodesV1TaxonomyNodesGet();
  const versionQ = useGetVersionV1TaxonomyVersionGet();
  const nodes = useMemo(() => nodesQ.data ?? [], [nodesQ.data]);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [importResult, setImportResult] = useState<TaxonomyImportResult | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const byParent = useMemo(() => {
    const m = new Map<string | null, TaxonomyNodeOut[]>();
    for (const n of nodes) {
      const k = n.parent_id;
      if (!m.has(k)) m.set(k, []);
      m.get(k)!.push(n);
    }
    for (const list of m.values())
      list.sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name));
    return m;
  }, [nodes]);

  const byId = useMemo(() => new Map(nodes.map((n) => [n.id, n] as const)), [nodes]);

  function pathOf(id: string | null): string {
    const names: string[] = [];
    let cur = id ? byId.get(id) : undefined;
    const seen = new Set<string>();
    while (cur && !seen.has(cur.id)) {
      seen.add(cur.id);
      names.unshift(cur.name);
      cur = cur.parent_id ? byId.get(cur.parent_id) : undefined;
    }
    return names.join(" > ") || "(root)";
  }

  // Descendant set of a node (to forbid moving a node under itself).
  function descendantsOf(id: string): Set<string> {
    const out = new Set<string>();
    const stack = [id];
    while (stack.length) {
      const cur = stack.pop()!;
      out.add(cur);
      for (const c of byParent.get(cur) ?? []) stack.push(c.id);
    }
    return out;
  }

  const invalidate = () => {
    qc.invalidateQueries({
      queryKey: getListNodesV1TaxonomyNodesGetQueryKey(),
    });
    qc.invalidateQueries({
      queryKey: getGetVersionV1TaxonomyVersionGetQueryKey(),
    });
  };

  const create = useCreateNodeV1TaxonomyNodesPost({
    mutation: {
      onSuccess: (n) => {
        invalidate();
        setSelectedId(n.id);
        setDraft(toDraft(n));
        notify("Node created");
      },
      onError: (e) => notify(errMsg(e, "Create failed"), "crit"),
    },
  });
  const update = useUpdateNodeV1TaxonomyNodesNodeIdPatch({
    mutation: {
      onSuccess: (n) => {
        invalidate();
        setDraft(toDraft(n));
        notify("Saved");
      },
      onError: (e) => notify(errMsg(e, "Save failed"), "crit"),
    },
  });
  const remove = useDeleteNodeV1TaxonomyNodesNodeIdDelete({
    mutation: {
      onSuccess: () => {
        invalidate();
        setSelectedId(null);
        setDraft(null);
        notify("Deleted");
      },
      onError: (e) => notify(errMsg(e, "Delete failed"), "crit"),
    },
  });
  const importMut = useImportTreeV1TaxonomyImportPost({
    mutation: { onError: (e) => notify(errMsg(e, "Import failed"), "crit") },
  });

  function select(n: TaxonomyNodeOut) {
    setSelectedId(n.id);
    setDraft(toDraft(n));
  }

  function toggle(id: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function save() {
    if (!draft) return;
    const name = draft.name.trim();
    if (!name) {
      notify("Name is required", "crit");
      return;
    }
    const sortOrder = Number(draft.sortOrder) || 0;
    if (draft.id) {
      const original = byId.get(draft.id);
      const moved = original ? original.parent_id !== draft.parentId : false;
      update.mutate({
        nodeId: draft.id,
        data: {
          name,
          description: draft.description,
          notes: draft.notes,
          sort_order: sortOrder,
          is_active: draft.isActive,
          // Only send parent_id on an actual move (avoids a needless depth recompute
          // + a misleading "moved" audit entry on every plain edit).
          ...(moved ? { parent_id: draft.parentId } : {}),
        },
      });
    } else {
      create.mutate({
        data: {
          parent_id: draft.parentId,
          name,
          description: draft.description,
          notes: draft.notes,
          sort_order: sortOrder,
          is_active: draft.isActive,
        },
      });
    }
  }

  function del() {
    if (!draft?.id) return;
    const node = byId.get(draft.id);
    const kids = node?.child_count ?? 0;
    const msg =
      kids > 0
        ? `Delete "${node?.name}" and its ${kids} child branch(es)? All descendants are removed.`
        : `Delete "${node?.name}"?`;
    if (!window.confirm(msg)) return;
    remove.mutate({
      nodeId: draft.id,
      params: kids > 0 ? { force: true } : {},
    });
  }

  async function onImportFile(file: File) {
    setImportResult(null);
    const preview = await importMut.mutateAsync({
      data: { file },
      params: { dry_run: true },
    });
    setImportResult(preview);
  }

  async function applyImport(file: File) {
    const res = await importMut.mutateAsync({
      data: { file },
      params: { dry_run: false },
    });
    invalidate();
    setImportResult(res);
    notify(`Imported: ${res.updated ?? 0} updated, ${(res.created ?? []).length} created`);
  }

  const pendingFile = fileRef.current?.files?.[0] ?? null;
  const ver = versionQ.data;
  const moveOptions = draft?.id ? descendantsOf(draft.id) : new Set<string>();

  function renderTree(parentId: string | null, depth: number): ReactNode {
    const kids = byParent.get(parentId) ?? [];
    return kids.map((n) => {
      const hasKids = (byParent.get(n.id)?.length ?? 0) > 0;
      const open = expanded.has(n.id);
      return (
        <div key={n.id}>
          <div
            className={`taxo-row ${selectedId === n.id ? "active" : ""}`}
            style={{
              paddingLeft: 8 + depth * 16,
              opacity: n.is_active ? 1 : 0.45,
            }}
            onClick={() => (hasKids ? toggle(n.id) : select(n))}
          >
            <span
              className={`taxo-caret${open ? "" : " collapsed"}`}
              role={hasKids ? "button" : undefined}
              aria-label={hasKids ? (open ? "collapse" : "expand") : undefined}
              onClick={(e) => {
                e.stopPropagation();
                if (hasKids) toggle(n.id);
              }}
            >
              {hasKids ? <IconChevronDown /> : null}
            </span>
            <span
              className="taxo-name"
              onClick={(e) => {
                e.stopPropagation();
                select(n);
              }}
            >
              {n.name}
              {!hasKids && <span className="taxo-leaf-tag">leaf</span>}
            </span>
            {hasKids && <span className="sub">{n.child_count}</span>}
          </div>
          {open && renderTree(n.id, depth + 1)}
        </div>
      );
    });
  }

  return (
    <div className="view">
      <PageHeader
        className="mb-3.5"
        title="Taxonomy"
        description={
          <>
            {ver ? (
              <>
                fingerprint <code>{ver.prompt_fingerprint}</code> &middot; {ver.node_count} nodes
                &middot; {ver.leaf_count} leaves
                {ver.stale_skus != null && ver.stale_skus > 0 && (
                  <>
                    {" "}
                    &middot;{" "}
                    <span className="text-warn">
                      {ver.stale_skus.toLocaleString()} SKUs stale
                    </span>
                  </>
                )}
              </>
            ) : (
              "loading..."
            )}
          </>
        }
        action={<Row gap="sm" className="flex-wrap justify-end">
          {canWrite && <Button
            variant="primary"
            loading={classify.isPending}
            title="Classify unlabelled + fingerprint-stale SKUs now, one run per platform"
            onClick={() => classify.mutate({ data: { mode: "missing" } })}
          >
            Classify now
          </Button>}
          {canWrite && <Button
            variant="outline"
            loading={classify.isPending}
            title="Force a full re-label of ALL active SKUs (expensive), one run per platform"
            onClick={() => {
              if (window.confirm("Reclassify ALL active SKUs on every platform? This is a full LLM pass."))
                classify.mutate({ data: { mode: "all" } });
            }}
          >
            Reclassify all
          </Button>}
          <Button asChild variant="outline">
            <Link to="/taxonomy/review">Review</Link>
          </Button>
          <Button asChild variant="outline">
            <a href="/v1/taxonomy/export">Export</a>
          </Button>
          {canWrite && (
            <Button variant="outline" onClick={() => fileRef.current?.click()}>
              Import
            </Button>
          )}
          {canWrite && <Button
            variant="primary"
            onClick={() => {
              setSelectedId(null);
              setDraft(newDraft(null));
            }}
          >
            <IconPlus /> Section
          </Button>}
          <input
            ref={fileRef}
            type="file"
            accept=".csv,text/csv"
            style={{ display: "none" }}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void onImportFile(f);
            }}
          />
        </Row>}
      />

      {importResult && (
        <Panel className="mb-3 p-3">
          <div className="row" style={{ justifyContent: "space-between" }}>
            <b>
              Import preview {importResult.applied ? "(applied)" : "(dry run)"} &mdash;{" "}
              {(importResult.created ?? []).length} created, {importResult.updated ?? 0} updated,{" "}
              {importResult.unchanged ?? 0} unchanged
            </b>
            <Row gap="sm">
              {!importResult.applied && pendingFile && (
                <Button variant="primary" onClick={() => void applyImport(pendingFile)}>
                  Apply import
                </Button>
              )}
              <Button variant="outline" onClick={() => setImportResult(null)}>
                Dismiss
              </Button>
            </Row>
          </div>
          {(importResult.warnings ?? []).length > 0 && (
            <ul className="sub" style={{ margin: "8px 0 0" }}>
              {(importResult.warnings ?? []).map((w) => (
                <li key={w}>! {w}</li>
              ))}
            </ul>
          )}
          {(importResult.created ?? []).length > 0 && (
            <div className="sub" style={{ marginTop: 8 }}>
              new: {(importResult.created ?? []).slice(0, 8).join(", ")}
              {(importResult.created ?? []).length > 8 ? " ..." : ""}
            </div>
          )}
        </Panel>
      )}

      <div className="grid" style={{ gridTemplateColumns: "minmax(280px, 360px) 1fr", gap: 16 }}>
        <Panel className="p-2 overflow-auto" style={{ maxHeight: "70vh" }}>
          {nodesQ.isLoading && !nodesQ.data ? (
            <SkeletonRegion label="Loading tree">
              <Stack gap="sm" className="p-4">
                {Array.from({ length: 3 }, (_, i) => (
                  <Skeleton key={i} h={28} />
                ))}
              </Stack>
            </SkeletonRegion>
          ) : nodesQ.isError ? (
            <ErrorState
              compact
              title="Could not load the taxonomy"
              message="Is the control plane up?"
              onRetry={() => void nodesQ.refetch()}
            />
          ) : nodes.length === 0 ? (
            <EmptyState compact title="No nodes" />
          ) : (
            renderTree(null, 0)
          )}
        </Panel>

        <Panel className="p-4">
          {!draft ? (
            <div className="sub">{canWrite ? "Select a node, or add a section." : "Select a node to inspect it."}</div>
          ) : (
            <Stack gap="md">
              <div>
                <div className="title">{draft.id ? "NODE" : "NEW NODE"}</div>
                <div className="sub">{pathOf(draft.parentId)}{draft.parentId ? " > " : ""}{draft.name || "(new)"}</div>
              </div>

              {/* `contents` keeps the Stack gaps; `disabled` makes every field read-only for viewers. */}
              <fieldset disabled={!canWrite} className="contents">
              <label className="col" style={{ gap: 4 }}>
                <span className="sub">Name</span>
                <input
                  className="input"
                  value={draft.name}
                  onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                />
              </label>

              <label className="col" style={{ gap: 4 }}>
                <span className="sub">Description (sent to the LLM; edits re-flag SKUs)</span>
                <textarea
                  className="input"
                  rows={4}
                  value={draft.description}
                  onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                />
              </label>

              <label className="col" style={{ gap: 4 }}>
                <span className="sub">Notes (operator-only; not sent to the LLM)</span>
                <textarea
                  className="input"
                  rows={2}
                  value={draft.notes}
                  onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
                />
              </label>

              <Row gap="md">
                <label className="col" style={{ gap: 4, maxWidth: 120 }}>
                  <span className="sub">Sort order</span>
                  <input
                    className="input"
                    value={draft.sortOrder}
                    onChange={(e) => setDraft({ ...draft, sortOrder: e.target.value })}
                  />
                </label>
                <label className="row" style={{ gap: 6, alignItems: "center" }}>
                  <input
                    type="checkbox"
                    checked={draft.isActive}
                    onChange={(e) => setDraft({ ...draft, isActive: e.target.checked })}
                  />
                  <span className="sub">Active</span>
                </label>
              </Row>

              {draft.id && (
                <label className="col" style={{ gap: 4 }}>
                  <span className="sub">Parent (move)</span>
                  <select
                    className="input"
                    value={draft.parentId ?? ""}
                    onChange={(e) => setDraft({ ...draft, parentId: e.target.value || null })}
                  >
                    <option value="">(root)</option>
                    {nodes
                      .filter((n) => !moveOptions.has(n.id))
                      .map((n) => (
                        <option key={n.id} value={n.id}>
                          {pathOf(n.id)}
                        </option>
                      ))}
                  </select>
                </label>
              )}
              </fieldset>

              {canWrite && <Row gap="sm">
                <Button
                  variant="primary"
                  onClick={save}
                  disabled={create.isPending || update.isPending}
                >
                  {draft.id ? "Save" : "Create"}
                </Button>
                {draft.id && (
                  <>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setSelectedId(null);
                        setDraft(newDraft(draft.id ?? null));
                      }}
                    >
                      <IconPlus /> Add child
                    </Button>
                    <Button
                      variant="outline"
                      loading={classify.isPending}
                      title="Re-label after editing this node. A full LLM pass over all active SKUs so items can move into/out of this node."
                      onClick={() => {
                        if (
                          window.confirm(
                            "Reclassify after editing this node? This runs a full LLM pass over ALL active SKUs (so items can move into/out of the node).",
                          )
                        )
                          classify.mutate({
                            data: { mode: "all", emphasis_node_id: draft.id },
                          });
                      }}
                    >
                      Reclassify
                    </Button>
                    <Button variant="destructive" onClick={del} disabled={remove.isPending}>
                      Delete
                    </Button>
                  </>
                )}
              </Row>}
            </Stack>
          )}
        </Panel>
      </div>
    </div>
  );
}
